import { Unsubscribe } from "redux";
import { CONFIG } from "~/config";
import Scene from "~/scene";
import { store } from "~/state/store";
import { watch, watchCoordinates, watchMapset } from "~/state/util";
import DrawCanvas from "./drawCanvas";
import WasmStoreGeometry from "~/wasm/store/wasm-store-geometry";
import { convertSmapStyleToWasmGeometryStyleHash } from "~/wasm/utils";

function ctrlPressed(event) {
	return event.ctrlKey || event.metaKey;
}

function getPixelCoords(coords) {
	const size = Scene.getSize();
	const px = Scene.coordsToPixel(coords);
	// eslint-disable-next-line id-length
	const x = Math.round(px.x + (size.x / 2));
	// eslint-disable-next-line id-length
	const y = Math.round(px.y + (size.y / 2));

	return { x, y };
}

/**
 * @class Nastroj pro kresleni do canvasove vrstvy pomoci mysi a prevadeni kresby na souradnice
 * pripadne pro nacitani souradnic a vykresleni do canvasove vrstvy
 */
export default class DrawTool {
	_storeUnsubscribe: Unsubscribe;

	_layerEnable: boolean;

	_enabled: boolean;

	_active: boolean;

	_eraseMod: boolean;

	_owner: any;

	_targetLayerId: any;

	_onUpdate: any;

	_coords: Array<any>;

	_actual: number;

	_resizeConstant: number;

	_moveEvnt: boolean;

	_canvasContext: any;

	_canvasElm: any;

	_layer: any;

	_storeMapUnsubscribe: () => void;

	_isWasm: boolean;

	_wasmGeometries: Array<WasmStoreGeometry>;

	/**
	 * @param [object || null] owner ktery vytvari instanci
	 * @param [int] targetLayerId identifikator vrstvy kam se kreslici layer pripne
	 * onUpdate?: (name: "clear"|"blur"|"draw-start")
	 */
	constructor(owner, targetLayerId, onUpdate) {
		this._owner = owner;
		this._targetLayerId = targetLayerId;
		this._onUpdate = onUpdate;
		this._coords = [];
		this._actual = 0;
		this._resizeConstant = 1.5;
		this._moveEvnt = false;
		this._canvasContext = null;
		this._canvasElm = null;
		this._layer = null;
		this._layerEnable = false;
		this._enabled = false;
		this._active = false;
		this._eraseMod = false;
		this._isWasm = CONFIG.wasmEnabled;
		this._wasmGeometries = [];
		this._init();

		// sledovani zmena mapsetu
		const wtMapset = watch(store.getState, "map", watchMapset);

		this._storeMapUnsubscribe = store.subscribe(wtMapset(() => {
			if (this._enabled) {
				this._reDraw();

				setTimeout(() => {
					this._reDraw();
				}, 250);
			}
		}));
	}

	destroy() {
		this.clear();
		this.disableLayer();
		this.disableDraw();
		this._storeMapUnsubscribe();
	}

	/* cele to vymazu */
	clear() {
		this._layer.clear();
		this._coords = [];
		this._actual = 0;

		this._owner?.drawClear();
		this._onUpdate && this._onUpdate("clear");

		for (const wasmGeometry of this._wasmGeometries) {
			wasmGeometry.destroy();
		}
	}

	/* vracim pole poli souradnic zkomprimovane Mirkovym algoritmem */
	getCoords() {
		const out = [];

		for (let index = 0; index < this._coords.length; index++) {
			const coords = this._coords[index];
			const params = [];

			for (let coordIndex = 2; coordIndex < coords.length; coordIndex++) {
				params.push(coords[coordIndex]);
			}

			const str = Scene.coordsToString(params);

			out.push([coords[0], coords[1], str]);
		}

		return out;
	}

	/* kreslim z pole souradnic zkomprimovanych Mirkovym algoritmem */
	drawFromCoords(coords) {
		const out = [];

		for (let index = 0; index < coords.length; index++) {
			const coord = Scene.stringToCoords(coords[index][2]);

			coord.splice(0, 0, coords[index][1]);
			coord.splice(0, 0, coords[index][0]);
			out.push(coord);
		}
		this._coords = out;

		this._actual = this._coords.length;
		this._reDraw();
	}

	/* aktivuji canvasouvou vrstvu */
	enableLayer() {
		if (this._layerEnable) {
			return;
		}
		!this._isWasm && Scene.addLayer(this._layer);
		this._layer.enable();
		const wt = watch(store.getState, "map", watchCoordinates);

		this._storeUnsubscribe = store.subscribe(wt((newMap, oldMap) => {
			if (
				newMap.longitude !== oldMap.longitude
				|| newMap.latitude !== oldMap.latitude
				|| newMap.zoom !== oldMap.zoom
			) {
				this._reDraw();
			}
		}));
		this._layerEnable = true;
	}

	isEnabled() {
		return this._enabled;
	}

	isEnabledLayer() {
		return this._layerEnable;
	}

	/* deaktivuji canvasovou vrstvu */
	disableLayer() {
		if (!this._layerEnable) {
			return;
		}
		this._layer.disable();
		!this._isWasm && Scene.removeLayer(this._layer);
		this._storeUnsubscribe?.();
		this._layerEnable = false;
	}

	/* aktivuji kresleni */
	enableDraw() {
		if (this._enabled) {
			return;
		}

		this._canvasElm.addEventListener("pointerdown", this, false);
		this._canvasElm.addEventListener("pointerup", this, false);
		this._canvasElm.addEventListener("pointerout", this, false);
		this._canvasElm.addEventListener("drop", this, false);
		this._enabled = true;
	}

	/* deaktivuji kresleni */
	disableDraw() {
		if (!this._enabled) {
			return;
		}
		this._canvasElm.removeEventListener("pointerdown", this, false);
		this._canvasElm.removeEventListener("pointerup", this, false);
		this._canvasElm.removeEventListener("pointerout", this, false);
		this._canvasElm.removeEventListener("drop", this, false);
		if (this._moveEvnt) {
			this._canvasContext.canvas.removeEventListener("pointermove", this, false);
			this._moveEvnt = false;
		}
		this._enabled = false;
	}

	startEraseMode(zoom?) {
		this._eraseMod = true;
		this._canvasContext.globalCompositeOperation = "destination-out";
		this._setStyle(zoom);
		//this._canvasContext.strokeStyle = '#00f';
	}

	endEraseMode(zoom) {
		this._eraseMod = false;
		this._canvasContext.globalCompositeOperation = "source-over";
		this._setStyle(zoom);
	}

	clearLast() {
		const last = this._wasmGeometries.pop();

		if (last) {
			last.destroy();
		}
	}

	handleEvent(event) {
		if (event.type === "pointerdown") {
			this._startMove(event);
		} else if (event.type === "pointerup" || event.type === "pointerout") {
			this._endMove(event);
		} else if (event.type === "pointermove") {
			this._move(event);
		} else if (event.type === "drop") {
			event.preventDefault();
			event.stopPropagation();
		}
	}

	/* inicializace vytvarim canvasovou vrstvu */
	_init() {
		this._layer = this._isWasm
			? new DrawCanvas()
			: Scene.createLayerCanvas("cvs", this._targetLayerId);

		// nastavime vetsi z-index - canvas bude nad markery a popupy
		this._layer.setStyle("zIndex", "100");
		this._layer.getContext(ctx => {
			this._canvasContext = ctx;
			this._canvasElm = this._canvasContext.canvas;
		}, this);
	}

	/* zacinam kreslit */
	async _startMove(event) {
		if (event.button !== 0) {
			return;
		}
		this._owner?.getForm().blur();
		this._onUpdate && this._onUpdate("blur");
		if (ctrlPressed(event)) {
			return;
		}
		event.stopPropagation(); /* musim, kvuli chrome, jinak mi pri mousemove zmeni kurzor */
		event.preventDefault();
		if (this._active) {
			return;
		}
		const { zoom } = store.getState().map;

		this._owner?.drawStart();
		this._onUpdate && this._onUpdate("draw-start");
		this._active = true;
		this._setStyle(zoom);

		const coords = await Scene.coordsFromEventAsync(event);

		this._coords.push([this._eraseMod ? "e" : "d", zoom]);
		this._coords[this._actual].push(coords);

		const newGeometry = new WasmStoreGeometry({
			type: "custom",
			strPoints: [],
			customStyleHash: {
				// @ts-ignore
				standard: convertSmapStyleToWasmGeometryStyleHash({
					color: "#ff0000",
					width: 6,
					keepExactWidth: false,
				}, true),
			},
		});

		this._drawCircle(coords);
		this._canvasContext.beginPath();
		this._wasmGeometries.push(newGeometry);
		this._canvasContext.canvas.addEventListener("pointermove", this, false);
		document.body.addEventListener("pointerup", this, false);
		this._moveEvnt = true;
	}

	/* koncim s kresbou jedne cary */
	_endMove(event) {
		if (event.button !== 0 || !this._active) {
			return;
		}

		this._active = false;
		this._canvasContext.canvas.addEventListener("pointermove", this, false);
		document.body.removeEventListener("pointerup", this, false);
		this._onUpdate && this._onUpdate("draw-end");
		this._moveEvnt = false;
		this._actual++;
	}

	/* kreslim */
	async _move(event) {
		if (!this._coords[this._actual]) {
			return;
		}

		event.stopPropagation();
		const coords = await Scene.coordsFromEventAsync(event);

		this._coords[this._actual].push(coords);
		this._draw(coords, this._coords[this._actual].length - 1);

		if (this._coords[this._actual].length > 1) {
			this._wasmGeometries[this._wasmGeometries.length - 1].update({
				// preskocime bordel na zacatku
				strPoints: [Scene.coordsToString(this._coords[this._actual].slice(2))],
			});
		}
	}

	/* prekresluji (po pohybu, prezumovani, ...) */
	_reDraw() {
		this._layer.clear();

		for (let index = 0; index < this._coords.length; index++) {
			const coor = this._coords[index];

			if (coor[0] === "e") {
				this.startEraseMode(coor[1]);
			} else {
				this.endEraseMode(coor[1]);
			}

			if (coor.length === 3) {
				this._drawCircle(coor[2], coor[1]);
				continue;
			}
			this._canvasContext.beginPath();

			for (let coorIndex = 2; coorIndex < coor.length; coorIndex++) {
				this._draw(coor[coorIndex], coorIndex);
			}
		}
	}

	_drawCircle(coords, zoomIn?: number) {
		if (this._isWasm) {
			return;
		}

		const { zoom } = store.getState().map;
		const zoomValid = typeof zoomIn === "undefined" ? zoom : zoomIn;

		this._canvasContext.beginPath();
		this._canvasContext.fillStyle = "#f00";
		const px = getPixelCoords(coords);
		let resizing = (zoom - zoomValid) * 3;

		if (!this._eraseMod && resizing < -3) {
			resizing = -3;
		} else if (resizing < -6) {
			resizing = -6;
		}

		this._canvasContext.arc(px.x, px.y, (this._eraseMod ? 6 : 3) + resizing, 0, 2*Math.PI);
		this._canvasContext.fill();
	}

	/* vlastni nakresleni useku */
	_draw(coords, index) {
		if (this._isWasm) {
			return;
		}

		const px = getPixelCoords(coords);

		if (index === 0) {
			this._canvasContext.moveTo(px.x, px.y);
		} else {
			this._canvasContext.lineTo(px.x, px.y);
		}
		this._canvasContext.stroke();
	}

	/* nastavuji styly kresby */
	_setStyle(zoomIn) {
		const { zoom } = store.getState().map;
		const zoomValid = typeof zoomIn === "undefined" ? zoom : zoomIn;

		this._canvasContext.strokeStyle = "#f00";
		//this._canvasContext.lineJoin = "round";

		let resizing = ((zoom - zoomValid) * this._resizeConstant) + 1;

		if (resizing === 0) {
			resizing = 0;
		}
		if (resizing < 0) {
			resizing = Math.abs(resizing)+1;
			resizing = 1/resizing;
		}

		this._canvasContext.lineWidth = 4 * resizing;

		if (this._eraseMod) {
			if (resizing <= 0.8) {
				resizing = 0.3;
			}
			this._canvasContext.lineWidth = 20 * resizing;
		}
	}
}
